/**
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * !IMPORTANT:
 *
 * This bntrk logic is mirrored in the BBWC.
 * Keep both in sync when making changes.
 * https://gitlab.bndl.nl/dh/bb-wc
 *
 * !IMPORTANT:
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 */

import * as Sentry from '@sentry/react';
import decode from './decode';

export type Bntrk = Record<string, string | string[]>;

const isExcludedKey = (key: string): boolean =>
  key.startsWith('bntrk') ||
  key.startsWith('dh:') ||
  key.startsWith('bndlData') ||
  key.startsWith('x-forwarded-host');

const collectParams = (params: URLSearchParams): Bntrk => {
  const bntrk: Bntrk = {};
  const seen = new Set<string>();

  params.forEach((_, key) => {
    if (seen.has(key)) return;
    seen.add(key);

    if (isExcludedKey(key)) return;

    const values = params.getAll(key).filter((v) => v.length < 255);
    if (values.length === 0) return;

    bntrk[key] = values.length === 1 ? values[0] : values;
  });

  return bntrk;
};

const generateBntrk = (
  params: URLSearchParams,
  keepOriginalBntrk: boolean = false,
): Bntrk => {
  const newParams = collectParams(params);

  if (!keepOriginalBntrk) return newParams;

  const encoded = params.get('bntrk');
  if (!encoded) return newParams;

  const original = decode(encoded);
  if (!original) return newParams;

  for (const key of Object.keys(newParams)) {
    if (key in original) {
      Sentry.captureException(
        new Error(`Duplicate bntrk key "${key}" — original value preserved`),
      );
    } else {
      original[key] = newParams[key];
    }
  }

  return original;
};

const getDevice = (): string =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  )
    ? 'mobile'
    : 'desktop';

const getReferrer = (): string | null => {
  try {
    const forwardedHost = new URLSearchParams(window.location.search).get(
      'x-forwarded-host',
    );
    if (forwardedHost) return forwardedHost;

    if (document.referrer) {
      const referrerHost = new URL(document.referrer).hostname;
      if (referrerHost !== window.location.hostname) return referrerHost;
    }
  } catch {
    // Malformed referrer
  }
  return null;
};

/**
 * Extends a bntrk object with environment data (dh:device, dh:lang, dh:ref)
 * and optional extra entries. Existing keys in `bntrk` are never overwritten.
 */
export const extendBntrk = (
  bntrk: Bntrk,
  extras?: Record<string, string>,
): Bntrk => {
  const env: Record<string, string> = {
    'dh:device': getDevice(),
    'dh:lang': navigator.language,
  };

  const ref = getReferrer();
  if (ref) env['dh:ref'] = ref;

  const merged = { ...bntrk };
  for (const [key, value] of Object.entries({ ...env, ...extras })) {
    if (!(key in merged)) {
      merged[key] = value;
    }
  }

  return merged;
};

export default generateBntrk;
